/**
 * React hooks of the inspector (W2-06). The inspector panel lives inside the editor shell, but its dialogs are
 * hosted at the app level (outside the DocStoreProvider), so every hook reads the store, the editor state, the
 * settings and the problems through `services` instead of context:
 *  - `useServicesStore`: the open project's DocStore (re-renders when a project opens or closes);
 *  - `useDocValue`: a document selector re-run only when one of its version topics changed (ARCHITECTURE §5.6);
 *  - `useEditorValue`, `useSettings`, `useLevel`, `useProblemList`: the editor store, the user settings, the
 *    experience level and the problems;
 *  - `usePlayheadSec`: the playhead for readouts ("value at the playhead"): updated on transport changes and polled
 *    4 times a second while a readout is shown (the playhead is never React state per frame, ARCHITECTURE §5.6).
 */

import { useCallback, useRef, useSyncExternalStore } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { Problem } from '../../core/check/types';
import type { ChartDoc, DocIndexes } from '../../core/model/types';
import type { Sec, TempoMap } from '../../core/time/types';
import { buildTempoMap } from '../../core/doc/container';
import type { Unsubscribe } from '../../core/util/types';
import { useDocValue, useSettings } from '../../app/registry/react';
import { useExternalSelector } from '../../state/react';
import type { DocStore, ExperienceLevel, VersionTopic } from '../../state/types';
export { useServicesStore, useDocValue, useEditorValue, useSettings } from '../../app/registry/react';

/**
 * A number that changes whenever `topic` changed (tables are updated in place, so selecting a whole table by
 * identity would never re-render).
 */
export function useTopicStamp(store: DocStore, topic: VersionTopic): number {
  const selector = useCallback(() => store.version(topic), [store, topic]);
  return useDocValue(store, selector, [topic]);
}

/** The experience level (Guided / Full; the settings service is the single source). */
export function useLevel(services: AppServices): ExperienceLevel {
  return useSettings(services).general.level;
}

/** The current problem list (re-read on every problems notification). */
export function useProblemList(services: AppServices): readonly Problem[] {
  const problems = services.problems;
  const version = useRef(0);
  const subscribe = useCallback(
    (cb: () => void): Unsubscribe =>
      problems.subscribe(() => {
        version.current++;
        cb();
      }),
    [problems],
  );
  const get = useCallback((): number => version.current, []);
  const select = useCallback((): readonly Problem[] => problems.problems(), [problems]);
  return useExternalSelector(problems, subscribe, get, select);
}

/** The tempo map of the document (rebuilt when the BPM table changes). */
export function useTempo(store: DocStore): TempoMap {
  return useDocValue(store, tempoSelector, ['bpm']);
}

function tempoSelector(doc: ChartDoc, idx: DocIndexes): TempoMap {
  return buildTempoMap(doc, idx);
}

// ---------------------------------------------------------------------------------------------------------------
// Playhead readout store
// ---------------------------------------------------------------------------------------------------------------

/** Refresh period of playhead readouts (ms). */
export const PLAYHEAD_READOUT_MS = 250;

interface PlayheadStore {
  readonly subscribe: (cb: () => void) => Unsubscribe;
  readonly get: () => Sec;
}

const playheadStores = new WeakMap<AppServices['audio'], PlayheadStore>();

function readPlayhead(services: AppServices): Sec {
  const t = services.audio.transport.chartTime();
  return Number.isFinite(t) && t > 0 ? Math.round(t * 1000) / 1000 : 0;
}

function playheadStore(services: AppServices): PlayheadStore {
  const audio = services.audio;
  const known = playheadStores.get(audio);
  if (known !== undefined) return known;
  const listeners = new Set<() => void>();
  let value = readPlayhead(services);
  let timer: ReturnType<typeof setInterval> | null = null;
  let offTransport: Unsubscribe | null = null;
  const refresh = (): void => {
    const next = readPlayhead(services);
    if (next === value) return;
    value = next;
    for (const cb of listeners) cb();
  };
  const store: PlayheadStore = {
    subscribe(cb) {
      listeners.add(cb);
      if (offTransport === null) {
        // Transport changes (play, stop, rate) answer at once; seeks from anywhere are caught by the poll.
        offTransport = audio.transport.onChange(refresh);
        timer = setInterval(refresh, PLAYHEAD_READOUT_MS);
        refresh();
      }
      return () => {
        listeners.delete(cb);
        if (listeners.size === 0) {
          offTransport?.();
          offTransport = null;
          if (timer !== null) clearInterval(timer);
          timer = null;
        }
      };
    },
    get: () => value,
  };
  playheadStores.set(audio, store);
  return store;
}

/** Chart time of the playhead (seconds, ms resolution) for readouts. */
export function usePlayheadSec(services: AppServices): Sec {
  const store = playheadStore(services);
  return useSyncExternalStore(store.subscribe, store.get, store.get);
}
